import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import Modal from '../shared/Modal'
import PushEventMeta from './PushEventMeta'
import PushEntryAggregate from './PushEntryAggregate'
import Pagination from '../shared/Pagination'

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
)

export default function EntriesTab({ selectedAccount }) {
  const [entries, setEntries] = useState([])
  const [events, setEvents] = useState([])
  const [tags, setTags] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [entryEvents, setEntryEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(false)

  // Filters
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [dateRange, setDateRange] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')

  // Pagination — server-side because PushEntries grows unboundedly. The
  // status / search filters still apply client-side on top of the loaded
  // page, which can narrow what's visible below pageSize. Date range and
  // tag filters apply server-side so totalCount stays accurate.
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Reset to page 1 whenever a server-side filter or the account changes.
  useEffect(() => {
    setPage(1)
  }, [selectedAccount, dateRange, tagFilter, pageSize])

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, page, pageSize, dateRange, tagFilter])

  useEffect(() => {
    if (selectedEntry) {
      loadEntryEvents(selectedEntry.PushEntryId)
    }
  }, [selectedEntry])

  const loadData = async () => {
    try {
      setLoading(true)

      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      // Build paginated query with server-side filters where possible.
      // Status / search are still client-side because they need a join
      // to events / fuzzy match. Date range and tag are exact and cheap.
      let query = supabase
        .from('PushEntries')
        .select('*', { count: 'exact' })
        .order('Created', { ascending: false })
        .range(from, to)

      if (selectedAccount) {
        query = query.eq('PushAccountId', selectedAccount.PushAccountId)
      }
      const startDate = getDateRangeStart(dateRange)
      if (startDate) {
        query = query.gte('Created', startDate.toISOString())
      }
      if (tagFilter !== 'all') {
        query = query.eq('TagId', Number(tagFilter))
      }

      const { data: entriesData, error: entriesError, count } = await query
      if (entriesError) throw entriesError

      setEntries(entriesData || [])
      setTotalCount(typeof count === 'number' ? count : 0)

      // Only load events for the entries on the current page so the
      // events query stays bounded in size.
      const entryIds = entriesData?.map(e => e.PushEntryId) || []
      if (entryIds.length > 0) {
        const { data: eventsData, error: eventsError } = await supabase
          .from('PushEntryDeliveryEvents')
          .select('*')
          .in('PushEntryId', entryIds)

        if (!eventsError) {
          setEvents(eventsData || [])
        }
      } else {
        setEvents([])
      }

      // Tags are loaded once (small table) and used for both label
      // rendering and the filter dropdown's full option list.
      const { data: tagsData } = await supabase
        .from('PushTags')
        .select('PushTagId, Tag')

      if (tagsData) {
        setTags(new Map(tagsData.map(t => [t.PushTagId, t.Tag])))
      }

    } catch (error) {
      console.error('Failed to load entries:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadEntryEvents = async (entryId) => {
    setEventsLoading(true)
    try {
      const { data, error } = await supabase
        .from('PushEntryDeliveryEvents')
        .select('*')
        .eq('PushEntryId', entryId)
        .order('Timestamp', { ascending: true })

      if (!error) {
        setEntryEvents(data || [])
      }
    } catch (err) {
      console.error('Failed to load entry events:', err)
    } finally {
      setEventsLoading(false)
    }
  }

  // Get latest status for an entry from events
  // - "pending" when no PushEntryDeliveryEvents row exists yet (the row will
  //   land when either Push-Send writes a no_targets event or the SW reports
  //   delivered/clicked back via Push-Webhook).
  // - otherwise the latest event's Status drives the badge.
  const getEntryStatus = (entryId) => {
    const entryEventsList = events.filter(e => e.PushEntryId === entryId)
    if (entryEventsList.length === 0) return 'pending'

    const sorted = [...entryEventsList].sort((a, b) =>
      new Date(b.Timestamp) - new Date(a.Timestamp)
    )
    return sorted[0]?.Status || 'pending'
  }

  // Get delivery count for an entry
  const getDeliveryCount = (entryId) => {
    return events.filter(e =>
      e.PushEntryId === entryId &&
      e.Status?.toLowerCase() === 'delivered'
    ).length
  }

  // Filter entries
  const getDateRangeStart = (range) => {
    if (range === 'all') return null
    const now = new Date()
    switch (range) {
      case 'today':
        return new Date(now.getFullYear(), now.getMonth(), now.getDate())
      case 'week':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      case 'month':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      default:
        return null
    }
  }

  // Tag dropdown options come from the full PushTags table (already
  // loaded), not just the current page's entries — otherwise paginating
  // would hide tags that exist on other pages.
  const tagsInUse = Array.from(tags.entries())
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label))

  // Server-side filters (date range, tag) are already applied by the
  // query. This client-side filter only handles status + search, which
  // narrow the loaded page further.
  const filteredEntries = entries.filter(entry => {
    // Status filter
    if (statusFilter !== 'all') {
      const status = getEntryStatus(entry.PushEntryId)
      if (status.toLowerCase() !== statusFilter.toLowerCase()) return false
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      const tag = tags.get(entry.TagId) || ''
      if (
        !entry.Title?.toLowerCase().includes(query) &&
        !entry.Body?.toLowerCase().includes(query) &&
        !tag.toLowerCase().includes(query)
      ) {
        return false
      }
    }

    return true
  })

  const getStatusBadge = (status) => {
    const statusLower = String(status || 'pending').toLowerCase()
    const badges = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
      sent: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
      no_targets: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
      delivered: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      clicked: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200',
      closed: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
      failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
    }
    return badges[statusLower] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  }

  const formatStatusLabel = (status) => {
    const lower = String(status || '').toLowerCase()
    if (lower === 'no_targets') return 'No targets'
    if (lower === 'pending') return 'Pending'
    if (!lower) return ''
    return lower.charAt(0).toUpperCase() + lower.slice(1)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Push Notification Log</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {selectedAccount ? `Showing entries for: ${selectedAccount.Name}` : 'Showing all entries'}
          </p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search title, body, or tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            title="Filter by latest status reported on the entry"
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="no_targets">No targets</option>
            <option value="delivered">Delivered</option>
            <option value="clicked">Clicked</option>
            <option value="closed">Dismissed</option>
            <option value="failed">Failed</option>
          </select>

          {/* Tag Filter */}
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            title="Filter by audience tag"
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 max-w-[14rem]"
          >
            <option value="all">All Tags</option>
            {tagsInUse.map(({ id, label }) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>

          {/* Date Range */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            title="Filter by entry creation time"
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>

          {/* Clear Filters */}
          {(statusFilter !== 'all' || searchQuery || dateRange !== 'all' || tagFilter !== 'all') && (
            <button
              onClick={() => {
                setStatusFilter('all')
                setSearchQuery('')
                setDateRange('all')
                setTagFilter('all')
              }}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Results count — shows page-narrowing impact of client-side
          (status + search) filters relative to what was returned by the
          server query. Server-side filters (date range, tag) shrink
          totalCount itself. */}
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Showing {filteredEntries.length} of {entries.length} on this page
        {filteredEntries.length !== entries.length && ' (filtered by status or search)'}
        {' · '}
        {totalCount.toLocaleString()} total{tagFilter !== 'all' || dateRange !== 'all' ? ' (filtered)' : ''}
      </p>

      {/* Entries Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
        {filteredEntries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Notification
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Tag
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Delivered
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEntries.map((entry) => {
                  const status = getEntryStatus(entry.PushEntryId)
                  const deliveryCount = getDeliveryCount(entry.PushEntryId)
                  const tag = tags.get(entry.TagId) || 'N/A'

                  return (
                    <tr key={entry.PushEntryId} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {entry.Title || 'No Title'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 max-w-xs">
                          {entry.Body}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 rounded">
                          {tag}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded ${getStatusBadge(status)}`}>
                          {formatStatusLabel(status)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {deliveryCount} device{deliveryCount !== 1 ? 's' : ''}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(entry.Created)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setSelectedEntry(entry)}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No notifications found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {entries.length > 0 ? 'No entries match your filters' : 'No push notifications sent yet'}
            </p>
          </div>
        )}
      </div>

      {totalCount > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          className="px-1"
        />
      )}

      {/* Entry Details Modal */}
      {selectedEntry && (
        <Modal title="Notification Details" onClose={() => setSelectedEntry(null)} size="lg">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title
                </label>
                <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                  {selectedEntry.Title || 'No Title'}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Body
                </label>
                <p className="text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 p-3 rounded">
                  {selectedEntry.Body || 'No Body'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tag / Audience
                </label>
                <span className="inline-block px-2 py-1 text-xs bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 rounded">
                  {tags.get(selectedEntry.TagId) || 'N/A'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Created At
                </label>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedEntry.Created)}</p>
              </div>
              {selectedEntry.ClickUrl && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Click URL
                  </label>
                  <a
                    href={selectedEntry.ClickUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline break-all"
                  >
                    {selectedEntry.ClickUrl}
                  </a>
                </div>
              )}
            </div>

            {/* Aggregate summary above the timeline */}
            {!eventsLoading && entryEvents.length > 0 && (
              <PushEntryAggregate events={entryEvents} />
            )}

            {/* Delivery Events Timeline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Delivery Events Timeline
              </label>
              {eventsLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                </div>
              ) : entryEvents.length > 0 ? (
                <div className="space-y-3">
                  {entryEvents.map((event, idx) => (
                    <div
                      key={event.PushEntryDeliveryEventId}
                      className="flex items-start space-x-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                    >
                      <div className="flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                          event.Status === 'delivered' ? 'bg-green-500' :
                          event.Status === 'clicked' ? 'bg-indigo-500' :
                          event.Status === 'closed' ? 'bg-gray-500' :
                          event.Status === 'failed' ? 'bg-red-500' :
                          event.Status === 'no_targets' ? 'bg-orange-500' : 'bg-yellow-500'
                        }`}>
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${getStatusBadge(event.Status)}`}>
                            {formatStatusLabel(event.Status)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(event.Timestamp)}
                          </span>
                        </div>
                        {event.DeviceToken && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                            Device: {event.DeviceToken.substring(0, 30)}...
                          </p>
                        )}
                        {event.Meta && Object.keys(event.Meta).length > 0 && (
                          <PushEventMeta status={event.Status} meta={event.Meta} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-4 rounded">
                  No delivery events recorded yet
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setSelectedEntry(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
